import React, { useState } from 'react';
import {
  DollarSign, Settings, Users, FileText, Calendar, TrendingUp,
  Edit2, Save, X, Check, AlertCircle, Download, Search, Filter,
  ChevronRight, Clock, CreditCard, Building, Package, RefreshCw
} from 'lucide-react';

// Mock data
const currentGlobalRate = 1.30;

const tenantBillingData = [
  {
    id: 'tenant-001',
    name: 'FastShip Jamaica',
    prefix: 'FS',
    customRate: null, // Uses global rate
    packagesThisWeek: 127,
    packagesLastWeek: 142,
    currentWeekCharges: 165.10,
    lastWeekCharges: 184.60,
    status: 'active',
    paymentStatus: 'current',
    lastPayment: 'Feb 26, 2026',
  },
  {
    id: 'tenant-002',
    name: 'Island Express',
    prefix: 'IE',
    customRate: 1.15, // Negotiated rate
    packagesThisWeek: 89,
    packagesLastWeek: 95,
    currentWeekCharges: 102.35,
    lastWeekCharges: 109.25,
    status: 'active',
    paymentStatus: 'current',
    lastPayment: 'Feb 26, 2026',
  },
  {
    id: 'tenant-003',
    name: 'Quick Courier JA',
    prefix: 'QC',
    customRate: null,
    packagesThisWeek: 56,
    packagesLastWeek: 48,
    currentWeekCharges: 72.80,
    lastWeekCharges: 62.40,
    status: 'active',
    paymentStatus: 'overdue',
    lastPayment: 'Feb 12, 2026',
  },
  {
    id: 'tenant-004',
    name: 'Caribbean Freight',
    prefix: 'CF',
    customRate: 1.20,
    packagesThisWeek: 203,
    packagesLastWeek: 187,
    currentWeekCharges: 243.60,
    lastWeekCharges: 224.40,
    status: 'active',
    paymentStatus: 'current',
    lastPayment: 'Feb 26, 2026',
  },
];

const weeklyInvoices = [
  { id: 'INV-PF-2026-09', week: 'Feb 24 - Mar 2, 2026', totalPackages: 475, totalAmount: 583.85, status: 'pending', dueDate: 'Mar 9, 2026' },
  { id: 'INV-PF-2026-08', week: 'Feb 17 - Feb 23, 2026', totalPackages: 472, totalAmount: 580.80, status: 'paid', paidDate: 'Feb 26, 2026' },
  { id: 'INV-PF-2026-07', week: 'Feb 10 - Feb 16, 2026', totalPackages: 445, totalAmount: 546.50, status: 'paid', paidDate: 'Feb 19, 2026' },
  { id: 'INV-PF-2026-06', week: 'Feb 3 - Feb 9, 2026', totalPackages: 489, totalAmount: 600.70, status: 'paid', paidDate: 'Feb 12, 2026' },
];

export default function PlatformBilling() {
  const [activeTab, setActiveTab] = useState('overview');
  const [globalRate, setGlobalRate] = useState(currentGlobalRate);
  const [editingRate, setEditingRate] = useState(false);
  const [tempRate, setTempRate] = useState(currentGlobalRate);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const totalPackagesThisWeek = tenantBillingData.reduce((sum, t) => sum + t.packagesThisWeek, 0);
  const totalRevenueThisWeek = tenantBillingData.reduce((sum, t) => sum + t.currentWeekCharges, 0);
  const totalPackagesLastWeek = tenantBillingData.reduce((sum, t) => sum + t.packagesLastWeek, 0);
  const overdueCount = tenantBillingData.filter(t => t.paymentStatus === 'overdue').length;

  const handleSaveRate = () => {
    setGlobalRate(tempRate);
    setEditingRate(false);
  };

  const filteredTenants = tenantBillingData.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openTenantModal = (tenant) => {
    setSelectedTenant(tenant);
    setShowTenantModal(true);
  };

  return (
    <div className="platform-billing-page" data-testid="platform-billing">
      {/* Header */}
      <div className="page-header-section">
        <div className="header-content">
          <h2>Platform Billing</h2>
          <p>Manage processing fees charged to courier tenants</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" data-testid="export-billing">
            <Download size={16} /> Export Report
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="billing-stats-grid">
        <div className="stat-card highlight" data-testid="stat-revenue">
          <div className="stat-icon">
            <DollarSign size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">${totalRevenueThisWeek.toFixed(2)}</span>
            <span className="stat-label">This Week's Revenue</span>
            <span className="stat-change positive">
              <TrendingUp size={12} /> vs ${tenantBillingData.reduce((sum, t) => sum + t.lastWeekCharges, 0).toFixed(2)} last week
            </span>
          </div>
        </div>

        <div className="stat-card" data-testid="stat-packages">
          <div className="stat-icon">
            <Package size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{totalPackagesThisWeek}</span>
            <span className="stat-label">Packages Processed</span>
            <span className="stat-sub">This week</span>
          </div>
        </div>

        <div className="stat-card" data-testid="stat-rate">
          <div className="stat-icon">
            <CreditCard size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">${globalRate.toFixed(2)}</span>
            <span className="stat-label">Global Rate</span>
            <span className="stat-sub">Per package</span>
          </div>
        </div>

        <div className={`stat-card ${overdueCount > 0 ? 'warning' : ''}`} data-testid="stat-overdue">
          <div className="stat-icon">
            <AlertCircle size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{overdueCount}</span>
            <span className="stat-label">Overdue Accounts</span>
            <span className="stat-sub">Require attention</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="billing-tabs">
        <button
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <TrendingUp size={16} /> Overview
        </button>
        <button
          className={`tab-btn ${activeTab === 'tenants' ? 'active' : ''}`}
          onClick={() => setActiveTab('tenants')}
        >
          <Users size={16} /> Tenant Billing
        </button>
        <button
          className={`tab-btn ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          <FileText size={16} /> Weekly Invoices
        </button>
        <button
          className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <Settings size={16} /> Rate Settings
        </button>
      </div>

      {/* Tab Content */}
      <div className="billing-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="overview-grid">
              {/* Current Week Summary */}
              <div className="overview-card">
                <div className="card-header">
                  <h3>Current Week Summary</h3>
                  <span className="week-label">Feb 24 - Mar 2, 2026</span>
                </div>
                <div className="summary-stats">
                  <div className="summary-row">
                    <span>Total Packages</span>
                    <span className="value">{totalPackagesThisWeek}</span>
                  </div>
                  <div className="summary-row">
                    <span>Global Rate Packages</span>
                    <span className="value">{tenantBillingData.filter(t => !t.customRate).reduce((sum, t) => sum + t.packagesThisWeek, 0)}</span>
                  </div>
                  <div className="summary-row">
                    <span>Custom Rate Packages</span>
                    <span className="value">{tenantBillingData.filter(t => t.customRate).reduce((sum, t) => sum + t.packagesThisWeek, 0)}</span>
                  </div>
                  <div className="summary-row total">
                    <span>Total Processing Fees</span>
                    <span className="value">${totalRevenueThisWeek.toFixed(2)} USD</span>
                  </div>
                </div>
              </div>

              {/* Billing Schedule */}
              <div className="overview-card">
                <div className="card-header">
                  <h3>Billing Schedule</h3>
                </div>
                <div className="schedule-info">
                  <div className="schedule-item">
                    <Clock size={18} />
                    <div>
                      <span className="schedule-label">Billing Cycle</span>
                      <span className="schedule-value">Weekly (Mon - Sun)</span>
                    </div>
                  </div>
                  <div className="schedule-item">
                    <Calendar size={18} />
                    <div>
                      <span className="schedule-label">Invoice Generated</span>
                      <span className="schedule-value">Every Monday</span>
                    </div>
                  </div>
                  <div className="schedule-item">
                    <CreditCard size={18} />
                    <div>
                      <span className="schedule-label">Payment Due</span>
                      <span className="schedule-value">7 days after invoice</span>
                    </div>
                  </div>
                  <div className="schedule-item highlight">
                    <RefreshCw size={18} />
                    <div>
                      <span className="schedule-label">Next Invoice</span>
                      <span className="schedule-value">Monday, Mar 3, 2026</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Tenants */}
              <div className="overview-card full-width">
                <div className="card-header">
                  <h3>Top Tenants by Volume</h3>
                  <button className="btn btn-ghost btn-sm" onClick={() => setActiveTab('tenants')}>
                    View All <ChevronRight size={14} />
                  </button>
                </div>
                <div className="top-tenants-list">
                  {[...tenantBillingData].sort((a, b) => b.packagesThisWeek - a.packagesThisWeek).slice(0, 4).map((tenant, i) => (
                    <div key={tenant.id} className="top-tenant-item">
                      <span className="rank">#{i + 1}</span>
                      <div className="tenant-info">
                        <span className="tenant-prefix" style={{ background: i === 0 ? '#f59e0b' : i === 1 ? '#94a3b8' : i === 2 ? '#b45309' : '#64748b' }}>
                          {tenant.prefix}
                        </span>
                        <span className="tenant-name">{tenant.name}</span>
                      </div>
                      <div className="tenant-stats">
                        <span className="packages">{tenant.packagesThisWeek} pkgs</span>
                        <span className="amount">${tenant.currentWeekCharges.toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tenants Tab */}
        {activeTab === 'tenants' && (
          <div className="tenants-tab">
            <div className="tenants-toolbar">
              <div className="search-box">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search tenants..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="tenants-table">
              <div className="table-header">
                <span>Tenant</span>
                <span>Rate</span>
                <span>This Week</span>
                <span>Charges</span>
                <span>Payment Status</span>
                <span>Actions</span>
              </div>
              {filteredTenants.map((tenant) => (
                <div key={tenant.id} className="table-row" data-testid={`tenant-${tenant.id}`}>
                  <div className="tenant-cell">
                    <span className="tenant-prefix-badge">{tenant.prefix}</span>
                    <div>
                      <span className="tenant-name">{tenant.name}</span>
                      <span className="tenant-id">{tenant.id}</span>
                    </div>
                  </div>
                  <div className="rate-cell">
                    <span className="rate-value">${(tenant.customRate || globalRate).toFixed(2)}</span>
                    {tenant.customRate ? (
                      <span className="rate-badge custom">Custom</span>
                    ) : (
                      <span className="rate-badge global">Global</span>
                    )}
                  </div>
                  <div className="packages-cell">
                    <span className="packages-value">{tenant.packagesThisWeek}</span>
                    <span className="packages-label">packages</span>
                  </div>
                  <div className="charges-cell">
                    <span className="charges-value">${tenant.currentWeekCharges.toFixed(2)}</span>
                  </div>
                  <div className="status-cell">
                    <span className={`payment-status ${tenant.paymentStatus}`}>
                      {tenant.paymentStatus === 'current' ? (
                        <><Check size={12} /> Current</>
                      ) : (
                        <><AlertCircle size={12} /> Overdue</>
                      )}
                    </span>
                    <span className="last-payment">Last: {tenant.lastPayment}</span>
                  </div>
                  <div className="actions-cell">
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => openTenantModal(tenant)}
                      data-testid={`edit-${tenant.id}`}
                    >
                      <Edit2 size={14} /> Manage
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div className="invoices-tab">
            <div className="invoices-list">
              {weeklyInvoices.map((invoice) => (
                <div key={invoice.id} className={`invoice-card ${invoice.status}`} data-testid={`invoice-${invoice.id}`}>
                  <div className="invoice-main">
                    <div className="invoice-info">
                      <span className="invoice-id">{invoice.id}</span>
                      <span className="invoice-week">{invoice.week}</span>
                    </div>
                    <div className="invoice-stats">
                      <div className="stat">
                        <Package size={14} />
                        <span>{invoice.totalPackages} packages</span>
                      </div>
                      <div className="stat amount">
                        <DollarSign size={14} />
                        <span>${invoice.totalAmount.toFixed(2)} USD</span>
                      </div>
                    </div>
                    <div className="invoice-status-section">
                      <span className={`invoice-status ${invoice.status}`}>
                        {invoice.status === 'paid' ? 'Paid' : 'Pending'}
                      </span>
                      {invoice.status === 'paid' ? (
                        <span className="status-date">Paid on {invoice.paidDate}</span>
                      ) : (
                        <span className="status-date">Due {invoice.dueDate}</span>
                      )}
                    </div>
                  </div>
                  <div className="invoice-actions">
                    <button className="btn btn-ghost btn-sm">
                      <FileText size={14} /> View Details
                    </button>
                    <button className="btn btn-ghost btn-sm">
                      <Download size={14} /> Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="settings-tab">
            <div className="settings-card">
              <div className="settings-header">
                <div>
                  <h3>Global Processing Fee Rate</h3>
                  <p>This rate applies to all tenants without a custom rate</p>
                </div>
              </div>
              
              <div className="rate-setting">
                {editingRate ? (
                  <div className="rate-edit-form">
                    <div className="rate-input-group">
                      <span className="currency">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={tempRate}
                        onChange={(e) => setTempRate(parseFloat(e.target.value) || 0)}
                        data-testid="rate-input"
                      />
                      <span className="unit">USD / package</span>
                    </div>
                    <div className="rate-actions">
                      <button className="btn btn-secondary" onClick={() => setEditingRate(false)}>
                        <X size={14} /> Cancel
                      </button>
                      <button className="btn btn-primary" onClick={handleSaveRate} data-testid="save-rate">
                        <Save size={14} /> Save Rate
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="rate-display">
                    <div className="current-rate">
                      <span className="rate-amount">${globalRate.toFixed(2)}</span>
                      <span className="rate-unit">USD per package</span>
                    </div>
                    <button
                      className="btn btn-secondary"
                      onClick={() => { setTempRate(globalRate); setEditingRate(true); }}
                      data-testid="edit-rate"
                    >
                      <Edit2 size={14} /> Edit Rate
                    </button>
                  </div>
                )}
              </div>

              <div className="rate-impact">
                <h4>Rate Change Impact</h4>
                <p>Changing the global rate will affect {tenantBillingData.filter(t => !t.customRate).length} tenants using the global rate.</p>
                <div className="impact-preview">
                  <div className="impact-item">
                    <span className="label">Current Weekly Revenue (Global Rate)</span>
                    <span className="value">${tenantBillingData.filter(t => !t.customRate).reduce((sum, t) => sum + t.currentWeekCharges, 0).toFixed(2)}</span>
                  </div>
                  {editingRate && tempRate !== globalRate && (
                    <div className="impact-item projected">
                      <span className="label">Projected Weekly Revenue</span>
                      <span className="value">${(tenantBillingData.filter(t => !t.customRate).reduce((sum, t) => sum + t.packagesThisWeek, 0) * tempRate).toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="settings-card">
              <div className="settings-header">
                <div>
                  <h3>Tenants with Custom Rates</h3>
                  <p>These tenants have negotiated rates different from the global rate</p>
                </div>
              </div>
              <div className="custom-rates-list">
                {tenantBillingData.filter(t => t.customRate).map((tenant) => (
                  <div key={tenant.id} className="custom-rate-item">
                    <div className="tenant-info">
                      <span className="tenant-prefix-badge">{tenant.prefix}</span>
                      <span className="tenant-name">{tenant.name}</span>
                    </div>
                    <div className="rate-info">
                      <span className="custom-rate">${tenant.customRate.toFixed(2)}</span>
                      <span className="rate-diff">
                        {tenant.customRate < globalRate ? (
                          <span className="discount">-${(globalRate - tenant.customRate).toFixed(2)} discount</span>
                        ) : (
                          <span className="premium">+${(tenant.customRate - globalRate).toFixed(2)} premium</span>
                        )}
                      </span>
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={() => openTenantModal(tenant)}>
                      <Edit2 size={14} />
                    </button>
                  </div>
                ))}
                {tenantBillingData.filter(t => t.customRate).length === 0 && (
                  <p className="empty-state">No tenants with custom rates</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tenant Management Modal */}
      {showTenantModal && selectedTenant && (
        <div className="modal-overlay" onClick={() => setShowTenantModal(false)}>
          <div className="modal-content tenant-billing-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>Manage Tenant Billing</h3>
                <span className="modal-subtitle">{selectedTenant.name}</span>
              </div>
              <button className="close-btn" onClick={() => setShowTenantModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="tenant-summary">
                <div className="summary-item">
                  <span className="label">Tenant ID</span>
                  <span className="value">{selectedTenant.id}</span>
                </div>
                <div className="summary-item">
                  <span className="label">This Week's Packages</span>
                  <span className="value">{selectedTenant.packagesThisWeek}</span>
                </div>
                <div className="summary-item">
                  <span className="label">This Week's Charges</span>
                  <span className="value">${selectedTenant.currentWeekCharges.toFixed(2)}</span>
                </div>
                <div className="summary-item">
                  <span className="label">Payment Status</span>
                  <span className={`value status ${selectedTenant.paymentStatus}`}>
                    {selectedTenant.paymentStatus === 'current' ? 'Current' : 'Overdue'}
                  </span>
                </div>
              </div>

              <div className="rate-section">
                <h4>Processing Fee Rate</h4>
                <div className="rate-options">
                  <label className="rate-option">
                    <input
                      type="radio"
                      name="rateType"
                      defaultChecked={!selectedTenant.customRate}
                    />
                    <div className="option-content">
                      <span className="option-label">Use Global Rate</span>
                      <span className="option-value">${globalRate.toFixed(2)} / package</span>
                    </div>
                  </label>
                  <label className="rate-option">
                    <input
                      type="radio"
                      name="rateType"
                      defaultChecked={!!selectedTenant.customRate}
                    />
                    <div className="option-content">
                      <span className="option-label">Custom Rate</span>
                      <div className="custom-rate-input">
                        <span>$</span>
                        <input
                          type="number"
                          step="0.01"
                          defaultValue={selectedTenant.customRate || globalRate}
                        />
                        <span>/ package</span>
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="billing-history">
                <h4>Recent Billing History</h4>
                <div className="history-list">
                  <div className="history-item">
                    <span className="week">Feb 17-23, 2026</span>
                    <span className="packages">{selectedTenant.packagesLastWeek} pkgs</span>
                    <span className="amount">${selectedTenant.lastWeekCharges.toFixed(2)}</span>
                    <span className="status paid">Paid</span>
                  </div>
                  <div className="history-item">
                    <span className="week">Feb 10-16, 2026</span>
                    <span className="packages">98 pkgs</span>
                    <span className="amount">$127.40</span>
                    <span className="status paid">Paid</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowTenantModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" data-testid="save-tenant-billing">
                <Save size={14} /> Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
